import React from "react"
import { Stack, Divider, Body, TextLink, colors, H2, H2Library, FlexContainer } from "@carrotfertility/carotene"
import useInsuranceDetails from "./context/useInsuranceDetails"
import InsurancePlanInformationTable from "./plan-information-table"
import { CSDTableContainer } from "components/views/reimbursements/educationmodal/CSDTable"
import { Spacer } from "components/views/atoms/Atoms"
import { useCurrentUser } from "components/context/user/UserContext"
import { useHistory } from "react-router-dom"
import InsuranceExpired from "./InsuranceExpired"
import Locations from "components/views/home/navigation"
import styled from "styled-components"
import { MultiPathSVG } from "components/views/SVG"
import Tooltip from "components/Tooltip"
import NoInsurance from "./NoInsurance"

const StyledTextLink = styled(TextLink)`
  border-bottom: 1px solid ${colors.base.carrotOrange};
`
const StyledDivider = styled(Divider)`
  width: 100%;
`
const HelpIconContainer = styled.span`
  display: inline-block;
  vertical-align: text-top;
  color="#e48400" 
`
const StyledList = styled.ul`
  margin-top: 10px;
  margin-left: 25px;
  list-style-position: inside;
`
const StyledListItem = styled.li`
  height: 25px;
  font-size: 16px;
  &:before {
    color: black;
  }
`

const SpacedDivider = (): JSX.Element => (
  <>
    <Spacer height={2} />
    <StyledDivider />
    <Spacer height={2} />
  </>
)

const QleExplanationMessage = (): JSX.Element => {
  const message =
    "A qualifying life event (QLE) allows you to enroll in health insurance outside the open enrollment period. QLEs include"
  const listOptions = ["loss of coverage", "household changes", "changes in residence", "and more"]

  return (
    <FlexContainer width="300px" padding="small" direction="column">
      <Body>{message}</Body>
      <StyledList>
        {listOptions.map((textItem, index) => (
          <StyledListItem key={index}>{textItem}</StyledListItem>
        ))}
      </StyledList>
    </FlexContainer>
  )
}

const InsuranceExpiredMessage = () => (
  <>
    <Spacer height={1.5} />
    <InsuranceExpired />
  </>
)

const DeductibleInfoMessage = () => (
  <>
    <Spacer height={1.5} />
    <Body>
      {
        "You and your partner will need to meet annual deductibles before you can be reimbursed for qualified medical expenses."
      }
    </Body>
  </>
)

const QleResetMessage = (): JSX.Element => {
  const history = useHistory()
  const handleQleResetClick = (): void => history.push(Locations.Home.QleEnrollmentActive)

  return (
    <>
      <Spacer height={1.5} />
      <Body>
        {"Has your plan changed due to a qualifying life event "}
        <Tooltip content={(): JSX.Element => <QleExplanationMessage />}>
          <HelpIconContainer>
            <MultiPathSVG iconName="icn-help" />
          </HelpIconContainer>
        </Tooltip>
        {", such as getting married or gaining a dependent? "}
        <StyledTextLink onClick={handleQleResetClick}>{"Update plan info."}</StyledTextLink>
      </Body>
    </>
  )
}

const NoInsuranceMessage = (): JSX.Element => (
  <>
    <Spacer height={2} />
    <NoInsurance />
  </>
)

const DeductibleTables = (): JSX.Element => (
  <>
    <Spacer height={2} />
    <CSDTableContainer />
  </>
)

const HealthPlanTable = (): JSX.Element => {
  const userInfo = useCurrentUser()
  const { insurancePlanDetailsData } = useInsuranceDetails()
  const history = useHistory()
  const handleQleResetClick = (): void => history.push(Locations.Home.QleEnrollmentActive)

  return (
    <>
      <SpacedDivider />
      <Stack sx={{ paddingBottom: "24px" }} spacing="small">
        <H2>About your health plan</H2>
        <Body>
          {"Need to make a correction? "}
          <StyledTextLink onClick={handleQleResetClick}>{"Update your answers."}</StyledTextLink>
        </Body>
      </Stack>
      <InsurancePlanInformationTable hasPartner={userInfo.memberHasLegalPartner} planData={insurancePlanDetailsData} />
    </>
  )
}

const InsuranceDetailsTable = (): JSX.Element => {
  const userInfo = useCurrentUser()
  const { insuranceProviderDetailsData } = useInsuranceDetails()
  const history = useHistory()
  const handleEditInsuranceAnswers = (): void => history.push(Locations.Home.InsuranceEditEnrollmentActive)
  return (
    <>
      <SpacedDivider />
      <Stack sx={{ paddingBottom: "24px" }} spacing="small">
        <H2>Connecting to your insurance provider</H2>
        <Body>
          {"Need to make a correction? "}
          <StyledTextLink onClick={handleEditInsuranceAnswers}>{"Update your answers."}</StyledTextLink>
        </Body>
      </Stack>
      <InsurancePlanInformationTable
        hasPartner={userInfo.memberHasLegalPartner}
        planData={insuranceProviderDetailsData}
      />
    </>
  )
}

const InsuranceDetails = (): JSX.Element => {
  const {
    needsBenefitEnrollment,
    insuranceExpired,
    hasDeductible,
    hasDeductibleRemaining,
    partnerPlanRequiresInsuranceProviderInfo,
    memberPlanRequiresInsuranceProviderInfo,
    hasInsurance
  } = useInsuranceDetails()

  const isEnrolled = !needsBenefitEnrollment

  return (
    <FlexContainer direction="column" sx={{ marginTop: "40px", maxWidth: "685px" }}>
      <H2Library>{"About your insurance"}</H2Library>
      {insuranceExpired && <InsuranceExpiredMessage />}
      {hasDeductibleRemaining && isEnrolled && <DeductibleInfoMessage />}
      {isEnrolled && <QleResetMessage />}
      {!hasInsurance && <NoInsuranceMessage />}
      {hasDeductible && <DeductibleTables />}
      {isEnrolled && hasInsurance && (
        <>
          <HealthPlanTable />
          {(partnerPlanRequiresInsuranceProviderInfo || memberPlanRequiresInsuranceProviderInfo) && (
            <InsuranceDetailsTable />
          )}
        </>
      )}
    </FlexContainer>
  )
}

export default InsuranceDetails
